'use client';

import { ScrollButton } from '@/uiComposites/layout/scrollInline/ScrollButton';
import {
	ScrollInline,
	scrollInlineTranslations,
} from '@/uiComposites/layout/scrollInline/ScrollInline';
import {
	measureBefore,
	measureAfter,
	scrollInlineWrapper,
	scrollInlineNegativeInlinePadding,
} from '@/uiComposites/layout/scrollInline/ScrollInline.css';
import { useShowScrollButtons } from '@/uiComposites/layout/scrollInline/useShowScrollButtons';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { createContext, ReactNode, RefObject, use } from 'react';
import { carouselItemInlineSize, carouselSnap } from './MarkupCarousel.css';

export const scrollLeft = (scrollAreaElement: HTMLDivElement) => {
	const scrollDelta = Math.min(
		scrollAreaElement.clientWidth,
		carouselItemInlineSize,
	);
	// scrollBy has janky behaviour on iOS Safari
	scrollAreaElement.scrollTo({
		left: scrollAreaElement.scrollLeft - scrollDelta,
		behavior: 'smooth',
	});
};

export const scrollRight = (scrollAreaElement: HTMLDivElement) => {
	const scrollDelta = Math.min(
		scrollAreaElement.clientWidth,
		carouselItemInlineSize,
	);
	// scrollBy has janky behaviour on iOS Safari
	scrollAreaElement.scrollTo({
		left: scrollAreaElement.scrollLeft + scrollDelta,
		behavior: 'smooth',
	});
};

const CarouselContext = createContext<
	RefObject<HTMLDivElement | null> | undefined
>(undefined);

export const ScrollInlineCarousel = ({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) => {
	const {
		measureBeforeRef,
		measureAfterRef,
		scrollAreaRef,
		showBeforeButton,
		showAfterButton,
	} = useShowScrollButtons();
	return (
		<CarouselContext value={scrollAreaRef}>
			<span
				className={classes(
					className,
					scrollInlineWrapper,
					scrollInlineNegativeInlinePadding,
					zIndex({ isolation: 'isolate' }),
				)}
			>
				<ScrollInline
					scrollAreaClassName={classes(carouselSnap)}
					ref={scrollAreaRef}
					measureBeforeSlot={
						<i className={classes(measureBefore)} ref={measureBeforeRef} />
					}
					measureAfterSlot={
						<i className={classes(measureAfter)} ref={measureAfterRef} />
					}
					scrollbarWidth="none"
					notOverflowing={
						showBeforeButton === false && showAfterButton === false
					}
					mask={
						showBeforeButton && showAfterButton
							? 'both'
							: showBeforeButton
								? 'before'
								: showAfterButton
									? 'after'
									: undefined
					}
				>
					{children}
				</ScrollInline>
				<ScrollButton.Shelf>
					<ScrollButton.Item
						side="before"
						isShowing={showBeforeButton ?? false}
						buttonLabel={
							scrollInlineTranslations['Scroll left in previous scroll area']
						}
						onClick={() => {
							const scrollAreaElement = scrollAreaRef.current;
							if (scrollAreaElement == null) return;

							scrollLeft(scrollAreaElement);
						}}
					/>
					<ScrollButton.Item
						side="after"
						isShowing={showAfterButton ?? false}
						buttonLabel={
							scrollInlineTranslations['Scroll right in previous scroll area']
						}
						onClick={() => {
							const scrollAreaElement = scrollAreaRef.current;
							if (scrollAreaElement == null) return;

							scrollRight(scrollAreaElement);
						}}
					/>
				</ScrollButton.Shelf>
			</span>
		</CarouselContext>
	);
};

export const SwipeButton = ({
	children,
	className,
}: {
	children?: ReactNode;
	className?: string;
}) => {
	const scrollAreaRef = use(CarouselContext);
	return (
		<button
			className={classes(className)}
			onClick={() => {
				if (scrollAreaRef?.current == null) return;

				scrollRight(scrollAreaRef.current);
			}}
		>
			{children}
		</button>
	);
};
